import type { CustomAttributeData } from '@risksmart-app/domain/src/types/common.types';
import type {
  InputMaybe,
  InsertImpactMutation,
} from '@risksmart-app/web-graphql-client/generated/graphql';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTRPC } from 'src/utils/trpc';

export interface InsertImpactTRPCInput {
  Name: string;
  Rationale?: InputMaybe<string>;
  RatingGuidance?: InputMaybe<string>;
  LikelihoodAppetite?: InputMaybe<number>;
  CustomAttributeData?: InputMaybe<CustomAttributeData>;
  OwnerUserIds: string[];
  OwnerGroupIds: string[];
}

/**
 * Maps TRPC mutation response to match the GraphQL mutation structure
 */
const mapTrpcResponseToGraphQL = (trpcData: {
  Id: string;
}): InsertImpactMutation => {
  return {
    insertImpactApi: {
      __typename: 'IdOutput',
      Id: trpcData.Id,
    },
  };
};

export const useInsertImpactTRPC = () => {
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  const mutation = useMutation({
    ...trpc.frontend.impact.insert.mutationOptions({
      onSuccess: async () => {
        await Promise.all([
          queryClient.invalidateQueries({
            queryKey: trpc.frontend.impact.impacts.queryKey(),
          }),
          queryClient.invalidateQueries({
            queryKey: trpc.frontend.impact.impactList.queryKey(),
          }),
          queryClient.invalidateQueries({
            queryKey: trpc.frontend.impact.impactCount.queryKey(),
          }),
        ]);
      },
    }),
  });

  return {
    insertImpact: async (
      variables: InsertImpactTRPCInput
    ): Promise<InsertImpactMutation> => {
      const result = await mutation.mutateAsync(variables);

      return mapTrpcResponseToGraphQL(result);
    },
    loading: mutation.isPending,
    error: mutation.error,
  };
};
